/**
 * Phase 5 — Onboarding Packet Import tab.
 *
 * Sub-pass 1: Upload → Parse → Review preview.
 * Sub-pass 2: Confirm & Create → employee + linked records + PDF attachment.
 *
 * Gated by `employees:add`. The Import action card hides the file picker for
 * any user without the cap (matches the existing CSV-import gating pattern).
 *
 * Review screen shows every extracted field — operator-editable — flagged
 * for missing/low-confidence values. Sensitive fields (SSN last-4, banking
 * last-4) display as masked text; the full SSN and full account/routing
 * numbers never come back from /preview, only the SSN last-4 fingerprint
 * for display. The full SSN is kept server-side in the preview cache and
 * persisted on confirm via the encrypted SSN path.
 *
 * Duplicate detection: /preview returns `duplicate_match` (ssn_last4 /
 * name_dob / name_only). The review screen shows a banner. The confirm
 * call passes `force_create_despite_duplicate` only if the operator
 * explicitly chose to proceed.
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Upload, Loader2, AlertTriangle, CheckCircle2, FileText, Lock, ShieldAlert, Users as UsersIcon, DollarSign, Building2, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { useCap } from '../components/auth/RequireCap';
import { extractErrorMessage } from '../lib/errors';

// Fields surfaced on the review screen. Order matters — top is identity,
// then contact, then operational, then EEO/confidential. Each row knows
// its label, optional helper text, and whether it should be wide.
const EMPLOYEE_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'middle_name', label: 'Middle Name' },
  { key: 'last_name', label: 'Last Name', required: true },
  { key: 'suffix', label: 'Suffix' },
  { key: 'preferred_name', label: 'Preferred Name' },
  { key: 'maiden_or_other_last_names', label: 'Maiden / Other Names' },
  { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
  { key: 'sex', label: 'Sex' },
  { key: 'marital_status', label: 'Marital Status' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone_primary', label: 'Phone (Primary)' },
  { key: 'phone_secondary', label: 'Phone (Secondary)' },
  { key: 'address', label: 'Address', wide: true },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip_code', label: 'ZIP Code' },
  { key: 'hire_date', label: 'Hire Date', type: 'date' },
  { key: 'tricorps_start_date', label: 'TriCorps Start Date', type: 'date' },
  { key: 'position', label: 'Position (free text)' },
  { key: 'post_type', label: 'Employee Type Description (from packet Position)' },
  { key: 'classification_description', label: 'Classification (W-2 / 1099 / etc.)' },
  { key: 'company', label: 'Company' },
  // Audit (2026-06-26): the labels stay read-only — they're the packet's
  // free-text values; the resolved IDs below are the real link targets.
  { key: 'department_label', label: 'Department (from packet)', readonly: true, hint: 'Free-text from the packet. The ID below auto-resolves only on exact, unambiguous match against Locations.' },
  { key: 'location_id', label: 'Department / Location (resolved)', hint: 'Pre-selected only on a single exact match. Clear or edit before Confirm.' },
  { key: 'supervisor_label', label: 'Supervisor (from packet)', readonly: true, hint: 'Free-text from the packet. The ID below auto-resolves only on exact, unambiguous match against the Employee directory.' },
  { key: 'direct_supervisor_id', label: 'Direct Supervisor (resolved)', hint: 'Pre-selected only on a single exact match. Clear or edit before Confirm.' },
];

const CONFIDENTIAL_FIELDS = [
  { key: 'ethnicity_race', label: 'Ethnicity / Race' },
  { key: 'disability', label: 'Disability', type: 'bool' },
  { key: 'veteran_us_armed_forces', label: 'Veteran', type: 'bool' },
  { key: 'eligible_to_work_us', label: 'Eligible to Work in US', type: 'bool' },
  { key: 'religious_preference', label: 'Religious Preference' },
  { key: 'active_or_retired_le', label: 'Active/Retired LE', type: 'bool' },
  { key: 'le_agency', label: 'LE Agency' },
  { key: 'active_duty_military', label: 'Active Duty Military', type: 'bool' },
  { key: 'weapon_brand', label: 'Weapon Brand' },
  { key: 'next_of_kin_employer', label: 'NoK Employer' },
];

function FieldRow({ def, value, flag, onChange, testIdPrefix = 'onb-field' }) {
  const isFlag = !!flag;
  const isDate = def.type === 'date';
  const isBool = def.type === 'bool';
  return (
    <div className={def.wide ? 'sm:col-span-3' : ''} data-testid={`${testIdPrefix}-${def.key}`}>
      <Label className="flex items-center gap-2">
        <span>{def.label}</span>
        {def.required && <span className="text-red-500 text-xs">*</span>}
        {isFlag && (
          <Badge variant="outline" className={flag === 'missing_required' ? 'border-red-300 text-red-600' : 'border-amber-300 text-amber-700'} data-testid={`${testIdPrefix}-flag-${def.key}`}>
            {flag === 'missing_required' ? 'required' : 'missing'}
          </Badge>
        )}
      </Label>
      {isBool ? (
        <select
          className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange(def.key, v === '' ? null : v === 'true');
          }}
          disabled={def.readonly}
        >
          <option value="">—</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      ) : (
        <Input
          type={isDate ? 'date' : 'text'}
          value={value ?? ''}
          onChange={(e) => onChange(def.key, e.target.value || null)}
          disabled={def.readonly}
          className="mt-1"
        />
      )}
      {def.hint && <p className="text-xs text-slate-400 mt-1">{def.hint}</p>}
    </div>
  );
}

export default function OnboardingImportTab() {
  const canImport = useCap('employees', 'add');
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // The server's parsed response.
  const [emp, setEmp] = useState({});           // Editable employee values.
  const [subRecords, setSubRecords] = useState({}); // banking / w4 / sw / comp / ec
  const [forceDup, setForceDup] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const flagsMap = preview?.flags || {};

  const startUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await onboardingAPI.preview(fd);
      setPreview(data);
      setEmp({ ...data.employee });
      setSubRecords({
        compensation: data.compensation,
        banking: data.banking,
        federal_w4: data.federal_w4,
        state_withholding: data.state_withholding,
        emergency_contact: data.emergency_contact,
      });
      setForceDup(false);
      toast.success('Packet parsed. Review the extracted data below.');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not parse PDF'));
    } finally {
      setUploading(false);
    }
  };

  const onFieldChange = (key, value) => setEmp((prev) => ({ ...prev, [key]: value }));
  const onSubChange = (group, key, value) => setSubRecords((prev) => ({
    ...prev,
    [group]: prev[group] ? { ...prev[group], [key]: value } : { [key]: value },
  }));

  const cancel = () => {
    setPreview(null); setEmp({}); setSubRecords({}); setFile(null); setForceDup(false);
  };

  const confirmCreate = async () => {
    setConfirming(true);
    try {
      // Strip server-only / display-only fields before send.
      const employee = { ...emp };
      delete employee.ssn_last_4;
      delete employee.department_label;
      delete employee.supervisor_label;
      const body = {
        preview_id: preview.preview_id,
        employee,
        compensation: subRecords.compensation,
        banking: subRecords.banking,
        federal_w4: subRecords.federal_w4,
        state_withholding: subRecords.state_withholding,
        emergency_contact: subRecords.emergency_contact,
        force_create_despite_duplicate: forceDup,
      };
      const { data } = await onboardingAPI.confirm(body);
      toast.success('Employee created from onboarding packet');
      navigate(`/employees/${data.employee_id}`);
    } catch (e) {
      const msg = extractErrorMessage(e, 'Could not create employee');
      toast.error(msg);
    } finally {
      setConfirming(false);
    }
  };

  if (!canImport) {
    return (
      <Card data-testid="onb-import-denied">
        <CardContent className="flex items-center gap-3 p-6">
          <Lock className="w-6 h-6 text-slate-400" />
          <div>
            <p className="font-medium text-slate-700">Onboarding import not available for your role</p>
            <p className="text-sm text-slate-500">Requires the <code className="px-1 py-0.5 bg-slate-200 rounded text-xs">employees:add</code> capability.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─ STAGE 1: Upload ─────────────────────────────────────────────────────
  if (!preview) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Upload Onboarding Packet</CardTitle>
          <CardDescription>
            Upload a completed eForms onboarding PDF. The data-bearing pages
            (Employee Information, EEO Self-Identification, Additional
            Questions) are parsed and shown for review. Nothing is created
            until you confirm.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <label className="flex-1 cursor-pointer">
              <div className={`flex items-center justify-center gap-3 p-6 border-2 border-dashed rounded-xl transition-colors ${file ? 'border-brand-wordmark bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <Upload className="w-6 h-6 text-slate-400" />
                <div className="text-center">
                  {file ? (
                    <p className="font-medium text-brand-wordmark">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>
                  ) : (
                    <>
                      <p className="font-medium text-slate-700">Click to select onboarding PDF</p>
                      <p className="text-sm text-slate-500">eForms packet — Hunter-style layout</p>
                    </>
                  )}
                </div>
              </div>
              <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="onb-file-input" />
            </label>
            <Button onClick={startUpload} disabled={!file || uploading} className="btn-navy h-12 px-6" data-testid="onb-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Parsing…' : 'Parse Packet'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─ STAGE 2: Review & Confirm ───────────────────────────────────────────
  const dup = preview.duplicate_match;
  return (
    <div className="space-y-6" data-testid="onb-review-screen">
      {/* Duplicate warning */}
      {dup && (
        <Alert variant="destructive" data-testid="onb-duplicate-alert">
          <ShieldAlert className="w-5 h-5" />
          <AlertTitle>Possible duplicate employee</AlertTitle>
          <AlertDescription>
            <p>
              Matched <strong>{dup.first_name} {dup.last_name}</strong>
              {dup.date_of_birth && ` (DOB ${dup.date_of_birth})`} by{' '}
              <code>{dup.match_type}</code>.
            </p>
            <label className="inline-flex items-center gap-2 mt-2 text-sm">
              <input type="checkbox" checked={forceDup} onChange={(e) => setForceDup(e.target.checked)} data-testid="onb-force-duplicate" />
              I&apos;ve verified this is intentional — create the new record anyway.
            </label>
          </AlertDescription>
        </Alert>
      )}

      {/* Identity / Contact / Operational */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UsersIcon className="w-5 h-5" /> Employee Record</CardTitle>
          <CardDescription>
            Pages parsed: {Object.entries(preview.pages_found).map(([k, v]) => `${k}:p${v}`).join(', ')}.
            {preview.ssn_present && <> SSN present (last 4: <code>{emp.ssn_last_4}</code>) — will be stored encrypted on confirm.</>}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {EMPLOYEE_FIELDS.map(def => (
              <FieldRow key={def.key} def={def} value={emp[def.key]} flag={flagsMap[def.key]} onChange={onFieldChange} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Confidential tab fields */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-amber-600" /> Confidential — EEO &amp; Additional Questions</CardTitle>
          <CardDescription>These land in the Confidential tab on the employee record (HR Confidential grant required to view there).</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {CONFIDENTIAL_FIELDS.map(def => (
              <FieldRow key={def.key} def={def} value={emp[def.key]} flag={flagsMap[def.key]} onChange={onFieldChange} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Linked sub-records */}
      {subRecords.emergency_contact && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Heart className="w-5 h-5 text-rose-600" /> Emergency Contact</CardTitle>
            <CardDescription>Will be created as a linked primary contact.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {['first_name','last_name','relationship','phone_primary','phone_secondary','email','address'].map(k => (
                <FieldRow
                  key={k}
                  def={{ key: k, label: k === 'address' ? 'Address (single line)' : k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), wide: k === 'address' }}
                  value={subRecords.emergency_contact[k]}
                  onChange={(_, v) => onSubChange('emergency_contact', k, v)}
                  testIdPrefix="onb-ec"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {subRecords.banking && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-emerald-600" /> Direct Deposit</CardTitle>
            <CardDescription>Account number and routing number will be encrypted on create. You can only see the parsed values here; once stored they require an audited reveal.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {['bank_name','bank_address','account_type','account_number','routing_number','allocation_type'].map(k => (
                <FieldRow
                  key={k}
                  def={{ key: k, label: k === 'bank_address' ? 'Bank Address (single line)' : k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), wide: k === 'bank_address' }}
                  value={subRecords.banking[k]}
                  onChange={(_, v) => onSubChange('banking', k, v)}
                  testIdPrefix="onb-bank"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {subRecords.compensation && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-blue-600" /> Compensation (New Hire)</CardTitle>
            <CardDescription>An initial dated compensation record will be created. Pay rate is encrypted at rest.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {['effective_date','pay_type','pay_rate','employee_type','accrual_type','pay_info_reason','flsa_exempt','rehire'].map(k => (
                <FieldRow
                  key={k}
                  def={{ key: k, label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), type: k.endsWith('_date') ? 'date' : (['flsa_exempt','rehire','driving_for_company','uses_personal_vehicle'].includes(k) ? 'bool' : 'text') }}
                  value={subRecords.compensation[k]}
                  onChange={(_, v) => onSubChange('compensation', k, v)}
                  testIdPrefix="onb-comp"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {subRecords.federal_w4 && (
        <Card>
          <CardHeader>
            <CardTitle>Federal W-4</CardTitle>
            <CardDescription>Initial dated W-4 election parsed from the packet&apos;s W-4 summary.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {['effective_date','tax_year','anticipated_filing_status','claim_exemption','multiple_jobs','qualifying_children','other_dependents','other_tax_credits','other_income','deductions','extra_withholding'].map(k => (
                <FieldRow
                  key={k}
                  def={{ key: k, label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), type: k === 'effective_date' ? 'date' : (['claim_exemption','multiple_jobs'].includes(k) ? 'bool' : 'text') }}
                  value={subRecords.federal_w4[k]}
                  onChange={(_, v) => onSubChange('federal_w4', k, v)}
                  testIdPrefix="onb-w4"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {subRecords.state_withholding && (
        <Card>
          <CardHeader>
            <CardTitle>State Withholding ({subRecords.state_withholding.withholding_state})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {['effective_date','withholding_state','filing_status','state_exemption','allowance_self','additional_allowances','additional_withholding'].map(k => (
                <FieldRow
                  key={k}
                  def={{ key: k, label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), type: k === 'effective_date' ? 'date' : (['state_exemption'].includes(k) ? 'bool' : 'text') }}
                  value={subRecords.state_withholding[k]}
                  onChange={(_, v) => onSubChange('state_withholding', k, v)}
                  testIdPrefix="onb-sw"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between border-t pt-4">
        <Button variant="outline" onClick={cancel} data-testid="onb-cancel-btn">Cancel</Button>
        <Button className="btn-navy"
          onClick={confirmCreate}
          disabled={confirming || (dup && !forceDup)}
          data-testid="onb-confirm-btn"
        >
          {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          {confirming ? 'Creating…' : 'Confirm & Create Employee'}
        </Button>
      </div>
    </div>
  );
}
